import { createContext, useContext, type ReactNode } from 'react';
import { useStore, type StoreApi } from 'zustand';
import type { BookingStore } from '../store/createBookingStore';

/**
 * Context for providing a booking store instance to a field's React tree.
 * Each booking field creates its own store instance for isolation.
 */
export const BookingStoreContext = createContext<StoreApi<BookingStore> | null>(
	null
);

interface BookingStoreProviderProps {
	store: StoreApi<BookingStore>;
	children: ReactNode;
}

/**
 * Provider component that supplies a booking store instance to the React tree.
 */
export function BookingStoreProvider({
	store,
	children,
}: BookingStoreProviderProps) {
	return (
		<BookingStoreContext.Provider value={store}>
			{children}
		</BookingStoreContext.Provider>
	);
}

/**
 * Hook to access the booking store from context.
 * This replaces the global useBookingStore hook.
 *
 * @param selector - Function to select a slice of the store state
 * @return The selected state
 */
export function useBookingStore<T>(selector: (state: BookingStore) => T): T {
	const store = useContext(BookingStoreContext);

	if (!store) {
		throw new Error(
			'useBookingStore must be used within a BookingStoreProvider'
		);
	}

	return useStore(store, selector);
}
